import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, KeyRound, UserMinus, UserPlus, X } from 'lucide-react';

import { ApiError, api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar } from '@/components/ui/avatar';
import { avatarUrl } from '@/lib/avatar-url';
import { useAuth } from '@/lib/use-auth';
import {
  ROLE_OPTIONS,
  roleLabel,
  type AdminScheduleSummary,
  type AdminUserDetail,
  type AdminUserSummary,
} from './types';
import type { Role } from '@/lib/auth-context';
import { AbsenceBalancesPanel } from './AbsenceBalancesPanel';
import { OvertimeBalancePanel } from './OvertimeBalancePanel';
import { AdminUserDocumentsPanel } from './AdminUserDocumentsPanel';
import { RegionSelect } from './RegionSelect';

export function AdminUserDetailPage() {
  const params = useParams<{ id: string }>();
  const userId = params.id ?? '';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user: viewer } = useAuth();
  const isSelf = viewer?.id === userId;
  // Feature flag del backend (/auth/me). Ausencia = habilitado.
  const documentsEnabled = viewer?.features?.documents ?? true;

  const detail = useQuery({
    queryKey: ['admin', 'users', userId],
    queryFn: () => api<AdminUserDetail>(`/admin/users/${userId}`),
    enabled: Boolean(userId),
  });

  const users = useQuery({
    queryKey: ['admin', 'users', { includeInactive: false }],
    queryFn: () => api<AdminUserSummary[]>('/admin/users'),
  });
  const schedules = useQuery({
    queryKey: ['admin', 'schedules'],
    queryFn: () => api<AdminScheduleSummary[]>('/admin/schedules'),
  });

  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState<Role>('EMPLOYEE');
  const [employeeCode, setEmployeeCode] = useState('');
  const [managerIds, setManagerIds] = useState<string[]>([]);
  const [scheduleId, setScheduleId] = useState<string>('');
  const [regionCode, setRegionCode] = useState<string>('ES-AN-SE');
  const [municipality, setMunicipality] = useState<string>('');

  useEffect(() => {
    if (!detail.data) return;
    setFullName(detail.data.fullName);
    setRole(detail.data.role);
    setEmployeeCode(detail.data.employeeCode ?? '');
    setManagerIds((detail.data.managers ?? []).map((m) => m.manager.id));
    const current = detail.data.scheduleAssignments?.[0]?.schedule.id ?? '';
    setScheduleId(current);
    setRegionCode(detail.data.region);
    setMunicipality(detail.data.municipality ?? '');
  }, [detail.data]);

  const saveMutation = useMutation({
    mutationFn: (overrides: Record<string, unknown> = {}) =>
      api<AdminUserDetail>(`/admin/users/${userId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          fullName: fullName.trim(),
          role,
          employeeCode: employeeCode.trim() || null,
          managerIds,
          scheduleId: scheduleId || null,
          regionCode,
          municipality,
          ...overrides,
        }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });

  const resetPwdMutation = useMutation({
    mutationFn: () =>
      api<{ ok: boolean }>(`/admin/users/${userId}/password-reset`, { method: 'POST' }),
  });

  const deleteMutation = useMutation({
    mutationFn: () => api<{ ok: boolean }>(`/admin/users/${userId}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      navigate('/admin/users');
    },
  });

  const reactivateMutation = useMutation({
    mutationFn: () =>
      api<AdminUserDetail>(`/admin/users/${userId}`, {
        method: 'PATCH',
        body: JSON.stringify({ isActive: true }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      detail.refetch();
    },
  });

  const managerCandidates = useMemo(
    () =>
      (users.data ?? []).filter(
        (m) =>
          m.id !== userId &&
          (m.role === 'MANAGER' || m.role === 'HR_ADMIN' || m.role === 'SUPER_ADMIN') &&
          m.isActive,
      ),
    [users.data, userId],
  );
  const managerById = useMemo(
    () => new Map(managerCandidates.map((m) => [m.id, m])),
    [managerCandidates],
  );
  const availableManagers = useMemo(
    () => managerCandidates.filter((m) => !managerIds.includes(m.id)),
    [managerCandidates, managerIds],
  );

  if (detail.isLoading) {
    return <div className="p-6 md:p-8 text-sm text-muted-foreground">Cargando…</div>;
  }
  if (detail.error) {
    return (
      <div className="p-6 md:p-8 text-sm text-destructive">
        {detail.error instanceof ApiError ? detail.error.message : 'Error cargando empleado'}
      </div>
    );
  }
  if (!detail.data) return null;

  const u = detail.data;
  const isInactive = !u.isActive || Boolean(u.deletedAt);

  function onSave(e: React.FormEvent) {
    e.preventDefault();
    saveMutation.mutate({});
  }

  function onResetPassword() {
    if (!confirm(`¿Resetear contraseña de ${u.fullName}? Recibirá email con la nueva temporal.`)) {
      return;
    }
    resetPwdMutation.mutate();
  }

  function onDelete() {
    if (!confirm(`¿Desactivar a ${u.fullName}? La sesión activa quedará invalidada.`)) return;
    deleteMutation.mutate();
  }

  const saveError = saveMutation.error
    ? saveMutation.error instanceof ApiError
      ? saveMutation.error.message
      : 'Error al guardar'
    : null;

  const selectCls =
    'h-9 w-full rounded-sm border border-input bg-card px-3 text-base text-foreground focus-visible:border-icaria-600 focus-visible:outline-none transition-colors disabled:opacity-50';

  return (
    <div className="p-6 md:p-8 max-w-3xl mx-auto">
      <div className="mb-3">
        <Link
          to="/admin/users"
          className="inline-flex items-center gap-1.5 text-[12px] font-medium text-muted-foreground hover:text-icaria-600 transition-colors"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
          Volver al listado
        </Link>
      </div>

      {/* Identity panel */}
      <div className="bg-card border border-border rounded-sm shadow-md overflow-hidden mb-5">
        <header className="px-7 py-5 border-b border-border flex items-center gap-4 flex-wrap">
          <Avatar
            fullName={u.fullName}
            color={u.profileColor}
            avatarUrl={avatarUrl(u.id, u.avatarUpdatedAt)}
            muted={isInactive}
            size="lg"
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2.5 flex-wrap">
              <h2 className="text-lg font-semibold tracking-tightish truncate">{u.fullName}</h2>
              {isInactive ? <Badge tone="neutral">Inactivo</Badge> : null}
              {u.passwordChangeRequired ? (
                <Badge tone="amber" dot>
                  Cambiar contraseña
                </Badge>
              ) : null}
            </div>
            <p className="text-[13px] text-muted-foreground mt-0.5">
              <span className="font-mono">{u.email}</span> · {roleLabel(u.role)}
            </p>
          </div>
        </header>

        <form onSubmit={onSave} className="px-7 py-6 flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="d-name">
              Nombre completo <span className="text-destructive">*</span>
            </Label>
            <Input
              id="d-name"
              type="text"
              required
              minLength={2}
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              disabled={isInactive}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="d-role">
                Rol <span className="text-destructive">*</span>
              </Label>
              <select
                id="d-role"
                value={role}
                onChange={(e) => setRole(e.target.value as Role)}
                className={selectCls}
                disabled={isInactive}
              >
                {ROLE_OPTIONS.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="d-code">Código de empleado</Label>
              <Input
                id="d-code"
                type="text"
                value={employeeCode}
                onChange={(e) => setEmployeeCode(e.target.value)}
                disabled={isInactive}
              />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Label>Managers</Label>
            {managerIds.length === 0 ? (
              <p className="text-[12px] text-muted-foreground">— Sin managers asignados —</p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {managerIds.map((id) => {
                  const m = managerById.get(id);
                  return (
                    <span
                      key={id}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm bg-icaria-100 text-icaria-700 text-[12px] font-medium border border-icaria-200"
                    >
                      {m ? m.fullName : id}
                      <button
                        type="button"
                        onClick={() => setManagerIds((curr) => curr.filter((x) => x !== id))}
                        disabled={isInactive}
                        className="hover:text-icaria-600 transition-colors disabled:opacity-50"
                        aria-label={`Quitar ${m?.fullName ?? id}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  );
                })}
              </div>
            )}

            {availableManagers.length > 0 ? (
              <select
                value=""
                onChange={(e) => {
                  const v = e.target.value;
                  if (v) setManagerIds((curr) => [...curr, v]);
                }}
                className={selectCls}
                disabled={isInactive}
              >
                <option value="">— Añadir manager —</option>
                {availableManagers.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.fullName} ({m.email})
                  </option>
                ))}
              </select>
            ) : (
              <p className="text-[12px] text-muted-foreground">
                No quedan más candidatos a manager.
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="d-schedule">Horario</Label>
              <select
                id="d-schedule"
                value={scheduleId}
                onChange={(e) => setScheduleId(e.target.value)}
                className={selectCls}
                disabled={isInactive}
              >
                <option value="">— Sin asignar —</option>
                {(schedules.data ?? []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="d-region">Provincia (calendario)</Label>
              <RegionSelect
                id="d-region"
                value={regionCode}
                onChange={setRegionCode}
                disabled={isInactive}
              />
              <span className="text-[11px] text-muted-foreground">
                Determina qué festivos se excluyen en sus vacaciones.
              </span>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="d-municipality">Municipio (opcional)</Label>
              <Input
                id="d-municipality"
                type="text"
                maxLength={120}
                value={municipality}
                onChange={(e) => setMunicipality(e.target.value)}
                disabled={isInactive}
                placeholder="Ej. Lebrija"
              />
              <span className="text-[11px] text-muted-foreground">
                Si lo rellenas, también se le aplican los festivos locales de ese municipio.
              </span>
            </div>
          </div>

          {saveError ? (
            <p role="alert" className="text-sm text-destructive">
              {saveError}
            </p>
          ) : null}

          <div className="flex items-center gap-2 pt-2">
            <Button type="submit" variant="primary" disabled={saveMutation.isPending || isInactive}>
              {saveMutation.isPending ? 'Guardando…' : 'Guardar cambios'}
            </Button>
          </div>
        </form>
      </div>

      {/* Saldos panel (own component) */}
      <div className="mb-5">
        <AbsenceBalancesPanel userId={userId} />
      </div>

      {/* Saldo de horas a compensar (own component) */}
      <div className="mb-5">
        <OvertimeBalancePanel userId={userId} />
      </div>

      {/* Documentos del empleado (subir, descargar, borrar) */}
      {documentsEnabled ? (
        <div className="mb-5">
          <AdminUserDocumentsPanel userId={userId} userName={detail.data?.fullName ?? ''} />
        </div>
      ) : null}

      {/* Acciones panel */}
      <div className="bg-card border border-border rounded-sm shadow-sm overflow-hidden">
        <header className="px-7 py-4 border-b border-border">
          <h2 className="text-base font-semibold tracking-tightish">Acciones</h2>
        </header>
        <div className="px-7 py-5">
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onResetPassword}
              disabled={resetPwdMutation.isPending || isInactive}
            >
              <KeyRound className="h-3.5 w-3.5" />
              {resetPwdMutation.isPending ? 'Reseteando…' : 'Resetear contraseña'}
            </Button>

            {isInactive ? (
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={() => reactivateMutation.mutate()}
                disabled={reactivateMutation.isPending}
              >
                <UserPlus className="h-3.5 w-3.5" />
                {reactivateMutation.isPending ? 'Reactivando…' : 'Reactivar'}
              </Button>
            ) : (
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={onDelete}
                disabled={deleteMutation.isPending || isSelf}
                title={isSelf ? 'No puedes desactivarte a ti mismo' : undefined}
              >
                <UserMinus className="h-3.5 w-3.5" />
                {deleteMutation.isPending ? 'Desactivando…' : 'Desactivar'}
              </Button>
            )}
          </div>

          {resetPwdMutation.isSuccess ? (
            <p className="mt-3 text-[13px] text-success">
              Contraseña reseteada. Se ha enviado un email con la nueva temporal.
            </p>
          ) : null}
          {resetPwdMutation.error ? (
            <p role="alert" className="mt-3 text-[13px] text-destructive">
              {resetPwdMutation.error instanceof ApiError
                ? resetPwdMutation.error.message
                : 'Error reseteando contraseña'}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
